import { useEffect, useState } from 'react';

import { get } from '../../../redux/api';
import type { GetInviteTokenResponse } from '../../../../typings/api';

type UseInviteToken = GetInviteTokenResponse;

/**
 * Accepts an invite token and handles fetching the org title
 * for that invite token, to display on the registration page.
 *
 * @param {(alert: string) => void} setAlert - A function to display an alert in case of an error.
 * @param {string} [inviteToken] - Invite token, taken from the query params (so may be undefined).
 */
export default function useInviteToken(setAlert: (alert: string) => void, inviteToken?: string): UseInviteToken {
  const [tokenInfo, setTokenInfo] = useState<UseInviteToken>({
    orgTitle: null,
    invitedEmail: null,
    postSignupRedirectUri: null,
  });

  useEffect(() => {
    async function getInviteToken() {
      if (inviteToken) {
        try {
          const { data } = await get<GetInviteTokenResponse>(`/api/invite-tokens/${inviteToken}`);
          setTokenInfo({
            orgTitle: data?.orgTitle || null,
            invitedEmail: data?.invitedEmail || null,
            postSignupRedirectUri: data?.postSignupRedirectUri || null,
          });
        } catch (requestError) {
          //  Handle cases where the token is expired, and tell the user that. The message is read
          //  defensively: a rejection without a `data` payload (a network failure, a non-JSON 5xx) would
          //  otherwise throw here before any alert is set, leaving the caller with neither an invited email
          //  nor an alert — a state the register page reads as "still resolving" and blocks signup on forever.
          const message = (requestError as { data?: { message?: string } } | null)?.data?.message;
          setAlert(message || 'Could not verify your invite. Please try again or sign up without an invite.');
        }
      }
    }
    getInviteToken();
  }, [setAlert, inviteToken]);

  return tokenInfo;
}
